import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Users, BedDouble, MapPin, CreditCard, AlertCircle, FileDown } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ManageBookingDetailPage() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token] = useState(() => sessionStorage.getItem("berdhill_self_service_token") || "");

  async function load() {
    setLoading(true);
    try {
      // Prefer authoritative read via token
      if (token) {
        const r = await api.get(`/bookings/self-service/${token}`);
        setBooking(r.data);
      } else {
        const cached = sessionStorage.getItem("berdhill_self_service_booking");
        if (cached) setBooking(JSON.parse(cached));
      }
    } catch {
      sessionStorage.removeItem("berdhill_self_service_token");
      sessionStorage.removeItem("berdhill_self_service_booking");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);

  if (loading) return <SiteLayout><div className="py-24 text-center">Loading…</div></SiteLayout>;
  if (!booking) {
    return (
      <SiteLayout>
        <div className="max-w-md mx-auto px-4 py-24 text-center">
          <AlertCircle className="mx-auto text-brand-error" size={42} />
          <h1 className="font-display text-3xl mt-4">Session expired</h1>
          <p className="text-brand-muted mt-2 text-sm">Please verify your booking again.</p>
          <Link to="/manage" className="inline-block mt-6 bg-brand-primary text-white px-6 py-3 rounded-sm text-sm">Back to /manage</Link>
        </div>
      </SiteLayout>
    );
  }

  async function cancel() {
    if (!confirm("Cancel this booking? Refunds (where applicable) follow our cancellation policy.")) return;
    try {
      // Use the same modify endpoint with status=cancelled if token-based, else fall through
      await api.post(`/bookings/${booking.id}/cancel-self-service`, { token });
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to cancel");
    }
  }

  return (
    <SiteLayout>
      <div className="max-w-3xl mx-auto px-4 py-16">
        <Link to="/manage" className="text-sm text-brand-muted hover:text-brand-text inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={14} /> Look up another booking
        </Link>

        <div className="bg-white border border-brand-border rounded-md overflow-hidden">
          <div className="bg-brand-secondary/40 p-6 border-b border-brand-border">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-muted">Reservation</div>
            <h1 className="font-display text-3xl mt-1">{booking.room_name}</h1>
            <div className="mt-3 flex items-baseline gap-6 flex-wrap text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-brand-muted">Reference</div>
                <div className="font-mono font-semibold tracking-wider">{booking.booking_ref || booking.id?.slice(0, 8)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-brand-muted">Status</div>
                <Tag value={booking.status} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-brand-muted">Payment</div>
                <Tag value={booking.payment_status} />
              </div>
            </div>
          </div>

          <div className="p-6 grid sm:grid-cols-2 gap-4 text-sm">
            <Row icon={<Calendar size={14} />} label="Check-in" value={booking.check_in} />
            <Row icon={<Calendar size={14} />} label="Check-out" value={booking.check_out} />
            <Row icon={<BedDouble size={14} />} label="Nights" value={booking.nights} />
            <Row icon={<Users size={14} />} label="Guests" value={booking.guests} />
            <Row icon={null} label="Subtotal" value={fmtMoney(booking.subtotal)} />
            <Row icon={null} label="Taxes" value={fmtMoney(booking.taxes)} />
            <Row icon={<CreditCard size={14} />} label="Total" value={<strong className="text-brand-primary">{fmtMoney(booking.total)}</strong>} />
            {booking.card_last4 && (
              <Row icon={<CreditCard size={14} />} label="Paid with" value={`${booking.card_brand || "card"} ····${booking.card_last4}`} />
            )}
          </div>

          {(booking.address_line || booking.city) && (
            <div className="border-t border-brand-border p-6 text-sm">
              <div className="text-[10px] uppercase tracking-wider text-brand-muted mb-2 flex items-center gap-1"><MapPin size={11} /> Billing address</div>
              <div className="text-brand-text">
                {booking.guest_name}<br />
                {booking.address_line && <>{booking.address_line}<br /></>}
                {booking.city} {booking.postal_code} {booking.country && `, ${booking.country}`}
              </div>
            </div>
          )}

          {booking.special_requests && (
            <div className="border-t border-brand-border p-6 text-sm">
              <div className="text-[10px] uppercase tracking-wider text-brand-muted mb-2">Special requests</div>
              <div className="text-brand-muted italic">"{booking.special_requests}"</div>
            </div>
          )}

          {booking.status !== "cancelled" && (
            <div className="border-t border-brand-border p-6 flex flex-wrap gap-3 justify-end">
              {token && (
                <a
                  href={`${BACKEND}/api/bookings/self-service/${token}/invoice`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="self-invoice-btn"
                  className="text-sm inline-flex items-center gap-2 border border-brand-border text-brand-text px-4 py-2.5 rounded-sm hover:bg-brand-bg"
                >
                  <FileDown size={14} /> Download invoice (PDF)
                </a>
              )}
              <button onClick={cancel} data-testid="self-cancel-btn" className="text-sm border border-rose-300 text-rose-700 px-4 py-2.5 rounded-sm hover:bg-rose-50">Cancel booking</button>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <span className="text-brand-muted flex items-center gap-1.5">{icon}{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

function Tag({ value }) {
  const map = {
    confirmed: "bg-emerald-50 text-emerald-700",
    paid: "bg-emerald-50 text-emerald-700",
    pending: "bg-amber-50 text-amber-700",
    unpaid: "bg-amber-50 text-amber-700",
    cancelled: "bg-rose-50 text-rose-700",
    failed: "bg-rose-50 text-rose-700",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm font-medium ${map[value] || "bg-zinc-100 text-zinc-700"}`}>{value}</span>;
}
